import { assert, assertNever } from '@varos/util-typescript';
// --- Span Transformation ---
function transformSpanToTiptapContent(span, containingBlockId // blockId is not used in this function currently, consider removing if not needed for future use
) {
    // Return type is correct: TiptapJSONContent[] essentially
    const marks = [];
    let textContent = '';
    if (span.kind === 'text') {
        textContent = span.text;
        if (span.annotations.bold) {
            marks.push({ type: 'bold' });
        }
        if (span.annotations.italic) {
            marks.push({ type: 'italic' });
        }
        if (span.annotations.underline) {
            marks.push({ type: 'underline' });
        }
        if (span.href) {
            // span.href is string | null from Core types
            marks.push({
                type: 'link',
                attrs: { href: span.href, target: '_blank' }, // TiptapMarkLinkAttrs expects href: string. Handle null.
            });
        }
        if (textContent) {
            return [
                {
                    type: 'text',
                    text: textContent,
                    marks: marks.length > 0 ? marks : undefined,
                },
            ];
        }
        return [];
    }
    else if (span.kind === 'mention') {
        const mentionSpan = span; // core MentionSpan
        // TiptapMentionNode expects attrs: { item: MentionSpan }
        return [
            {
                type: 'mention', // This is TiptapMentionNode
                attrs: {
                    item: mentionSpan, // Pass the core MentionSpan directly
                },
                // content is not allowed on TiptapMentionNode, so omit it.
            },
        ];
    }
    // else if (span.kind === 'reference') {
    //     throw new Error('Reference span not implemented for Tiptap conversion');
    // }
    else {
        // This will cause a compile error if AnySpan has new kinds not handled.
        // However, if span has an unexpected 'kind' string value not in the union, this assertNever won't catch it at compile time.
        // The runtime behavior is it tries to access span.kind and might error or proceed.
        // For robust runtime handling of unexpected kinds, an explicit check might be better.
        // assertNever(span, `Unsupported span kind: ${(span as any).kind}`);
        console.warn('Unsupported span kind:', span.kind);
        return [{ type: 'text', text: `[Unsupported span: ${span.kind}]` }];
    }
}
// --- Block Transformation ---
function transformBlock(block) {
    // Return type is the TiptapNode discriminated union
    if (block.kind === 'text') {
        return {
            type: 'textBlock',
            attrs: {
                blockId: block.id,
            },
            content: block.content.flatMap((span) => transformSpanToTiptapContent(span, block.id)),
        };
    }
    if (block.kind === 'heading') {
        return {
            type: 'headingBlock',
            attrs: {
                blockId: block.id,
                level: block.level, // Core level is 1 | 2 | 3. Tiptap allows 1-6.
                // Ensure mapping if core and Tiptap levels differ significantly.
            },
            content: block.content.flatMap((span) => transformSpanToTiptapContent(span, block.id)),
        };
    }
    if (block.kind === 'list_item') {
        // This will produce a TiptapListItemNode
        return {
            type: 'listItem',
            attrs: {
                blockId: block.id,
            },
            // TiptapListItemNode's content is Array<TiptapNode | TiptapTextNode>
            // Typically, this content should be a paragraph wrapping the actual list item spans.
            content: [
                {
                    type: 'paragraph', // Standard Tiptap practice: list items contain paragraphs
                    attrs: { blockId: `${block.id}-p` }, // Optional: give internal paragraph a related ID
                    content: block.content.flatMap((span) => transformSpanToTiptapContent(span, block.id)),
                },
            ],
        }; // Explicitly cast to TiptapListItemNode
    }
    if (block.kind === 'divider') {
        return {
            type: 'dividerBlock',
            attrs: {
                blockId: block.id,
            },
            // content is not allowed on TiptapDividerBlockNode
        };
    }
    if (block.kind === 'callout') {
        return {
            type: 'calloutBlock',
            attrs: {
                blockId: block.id,
                icon: block.icon || null, // If block.icon is '' or null, Tiptap icon becomes null
            },
            content: block.content.flatMap((span) => transformSpanToTiptapContent(span, block.id)),
        };
    }
    if (block.kind === 'custom') {
        // block is Core CustomNode
        // TiptapCustomBlockNode expects attrs: { node: CustomNode }
        return {
            type: 'customBlock',
            attrs: {
                node: block, // Pass the core CustomNode directly
            },
            // content is not allowed on TiptapCustomBlockNode, so omit it.
        };
    }
    // If execution reaches here, TypeScript should have narrowed block to `never`
    // if all block kinds in the AnyNode discriminated union are handled above.
    // This provides compile-time exhaustive checking.
    console.error(`Unsupported block kind encountered in transformBlock: ${block.kind}. This will throw an error via assertNever.`);
    return assertNever(block);
}
export function buildTiptapState(state) {
    // Return TiptapDocumentNode
    const content = []; // Content of the document root
    let i = 0;
    while (i < state.nodes.length) {
        const currentBlock = state.nodes[i];
        assert(currentBlock, 'Current block is undefined');
        if (currentBlock?.kind === 'list_item') {
            const collectedListItems = []; // Explicitly TiptapListItemNode[]
            const firstListItemBlock = currentBlock;
            const listStyle = firstListItemBlock.style; // 'number' | 'bullet' from core
            const tiptapListType = listStyle === 'number' ? 'orderedList' : 'bulletList';
            const listAttributes = { blockId: currentBlock.id }; // BlockId for the list wrapper itself
            while (i < state.nodes.length) {
                const blockBeingChecked = state.nodes[i];
                if (blockBeingChecked?.kind === 'list_item' &&
                    blockBeingChecked?.style === listStyle) {
                    // transformBlock for a list_item returns TiptapListItemNode
                    const transformedNode = transformBlock(blockBeingChecked);
                    collectedListItems.push(transformedNode);
                    i++;
                }
                else {
                    break;
                }
            }
            if (collectedListItems.length > 0) {
                if (tiptapListType === 'orderedList') {
                    content.push({
                        type: 'orderedList',
                        attrs: listAttributes,
                        content: collectedListItems, // This is TiptapListItemNode[] as required
                    });
                }
                else {
                    // bulletList
                    content.push({
                        type: 'bulletList',
                        attrs: listAttributes,
                        content: collectedListItems, // This is TiptapListItemNode[] as required
                    });
                }
            }
        }
        else {
            const transformedNode = transformBlock(currentBlock);
            content.push(transformedNode);
            i++;
        }
    }
    return {
        type: 'doc',
        content: content,
    };
}
//# sourceMappingURL=parseHelper.js.map